'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ensureServiceWorker } from '@/lib/service-worker';
import {
  readVoiceCallNotificationEnabled,
  writeVoiceCallNotificationEnabled,
} from '@/lib/voice-call-notification-preference';
import { isInternalExtension } from './phone-format';
import { useCustomerByPhone } from './use-customer-by-phone';
import type { ActiveCall, WebphoneState } from './webphone-types';

export type VoiceNotificationPermission = 'unsupported' | NotificationPermission;

/** Estado que o botao do painel do webphone precisa exibir. */
export type VoiceNotificationUiState = 'unsupported' | 'off' | 'on' | 'denied';

export interface UseIncomingCallNotificationArgs {
  state: WebphoneState;
  call: ActiveCall | null;
  /** Mesmo caminho do botao Atender do modal/card. */
  onAccept: () => void;
  /** Mesmo caminho do botao Recusar do modal/card. */
  onReject: () => void;
}

export interface UseIncomingCallNotificationResult {
  permission: VoiceNotificationPermission;
  enabled: boolean;
  uiState: VoiceNotificationUiState;
  /** SO chamar em gesto do agente (clique). Ver comentario em requestPermission. */
  requestPermission: () => Promise<void>;
  setEnabled: (next: boolean) => void;
}

/**
 * `actions` (os botoes Atender/Recusar) so existe em notificacao emitida pelo
 * Service Worker, e o `lib.dom.d.ts` do TypeScript 5.9 nao declara o campo em
 * `NotificationOptions` — ele vive no `lib.webworker.d.ts`, que este app nao
 * carrega. Tipo local em vez de `@ts-ignore` (proibido sem justificativa).
 */
interface CallNotificationOptions extends NotificationOptions {
  actions?: { action: string; title: string }[];
}

/** Uma tag por chamada: reemitir com a MESMA tag substitui, nao empilha. */
export function callNotificationTag(callId: string): string {
  return `voice-call-${callId}`;
}

/**
 * A aba esta fora do campo de visao do agente? (#435/#442)
 *
 * `hidden` cobre aba em segundo plano/janela minimizada. `!hasFocus()` cobre a
 * queixa que originou a feature: a aba esta a mostra, mas o agente esta
 * trabalhando em OUTRA janela — o ERP, o WhatsApp, outro navegador.
 */
function estaEmSegundoPlano(): boolean {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'hidden' || !document.hasFocus();
}

function lerPermissao(): VoiceNotificationPermission {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  if (!('serviceWorker' in navigator)) return 'unsupported';
  return Notification.permission;
}

async function mostrarNotificacao(args: {
  title: string;
  body: string;
  tag: string;
  callId: string;
}): Promise<boolean> {
  const reg = await ensureServiceWorker();
  if (!reg) return false;

  const options: CallNotificationOptions = {
    body: args.body,
    icon: '/hubinho-azul.png',
    badge: '/hubinho-azul.png',
    tag: args.tag,
    // A ligacao toca ate alguem atender: a notificacao nao pode sumir sozinha.
    requireInteraction: true,
    actions: [
      { action: 'accept', title: 'Atender' },
      { action: 'reject', title: 'Recusar' },
    ],
    // `type: 'voice-call'` e o que faz o SW NAO cair no ramo de navegacao — que
    // recarrega a pagina, dispara pagehide -> cleanupSip e mata a chamada. E
    // NAO ha `url` de proposito: sem url o ramo errado nem por acidente pega.
    data: { type: 'voice-call', callId: args.callId },
  };

  try {
    await reg.showNotification(args.title, options);
    return true;
  } catch (err) {
    // Isto NAO e o caminho de permissao negada — esse gate roda antes, no
    // efeito que chama esta funcao, e nunca chega aqui. Chegar neste catch e
    // falha INESPERADA (browser rejeita `actions`, ou a notificacao inteira).
    // Sem sinal nenhum, ninguem percebe que o aviso parou: o fallback (modal,
    // card, ringtone) continua rodando, entao o silencio nunca vira queixa.
    console.warn('[voice] falha ao mostrar notificacao de chamada', err);
    return false;
  }
}

async function fecharNotificacao(tag: string): Promise<void> {
  try {
    const reg = await ensureServiceWorker();
    if (!reg) return;
    const abertas = await reg.getNotifications({ tag });
    abertas.forEach((n) => n.close());
  } catch (err) {
    // Idem acima: falha inesperada em getNotifications/close, nao o caminho
    // silencioso de permissao negada (que nem chama esta funcao).
    console.warn('[voice] falha ao fechar notificacao de chamada', err);
  }
}

/**
 * Aviso nativo do sistema pra chamada entrante com a aba em segundo plano (#435).
 *
 * Mora AQUI e nao dentro do `use-webphone.ts` porque aquele hook nao tem arquivo
 * de teste. Um efeito sobre `state === 'ringing' && direction === 'inbound'` cobre
 * mostrar E esconder pra quase todas as saidas do toque — Terminated,
 * acceptIncoming, rejectIncoming, hangup e o caminho mock — porque todas elas
 * passam por `setState('registered')`, o que dispara a limpeza do efeito.
 *
 * `pagehide` e a EXCECAO, e por isso ganhou um segundo efeito so pra ele: o
 * listener de pagehide do proprio `use-webphone.ts` (`use-webphone.ts:163-166`)
 * chama SO `cleanupSip('pagehide')` — NUNCA `setState`. O `state` do React
 * continua `'ringing'` enquanto a pagina descarrega, entao o efeito de
 * mostrar/esconder acima nunca roda a limpeza nesse caminho. E nao da pra contar
 * com o desmonte do componente como rede de seguranca: unload real (F5, fechar
 * aba, sair da origem) nao desmonta a arvore React de forma confiavel — e por
 * isso que o proprio `use-webphone.ts` registra um listener PROPRIO de pagehide
 * em vez de confiar em unmount (comentario dele, linha 161: "Teardown SIP so via
 * disconnect() explicito ou pagehide"). Sem este segundo listener aqui, a
 * notificacao — que tem `requireInteraction: true` — fica presa na tela do
 * sistema depois que a sessao SIP ja morreu: o agente ve aviso de uma ligacao
 * que nao existe mais, e clicar em "Atender" nao faz nada.
 *
 * NAO usar `useTabBadge()`/`useNotifications()` aqui: o `WebphoneProvider` e
 * montado no `VoiceShell` do layout raiz, ACIMA dos providers do dashboard —
 * esses hooks voltariam no-op SILENCIOSO.
 */
export function useIncomingCallNotification({
  state,
  call,
  onAccept,
  onReject,
}: UseIncomingCallNotificationArgs): UseIncomingCallNotificationResult {
  const [permission, setPermission] = useState<VoiceNotificationPermission>('unsupported');
  const [enabled, setEnabledState] = useState(true);
  // callId da notificacao ATUALMENTE na tela. Tambem e o gate do lookup no ERP:
  // so buscamos o nome quando ha notificacao pra enriquecer.
  const [shownCallId, setShownCallId] = useState<string | null>(null);

  // Leitura no efeito, nao no render: `Notification` e `localStorage` nao
  // existem no server, e o componente renderiza no server tambem.
  useEffect(() => {
    setPermission(lerPermissao());
    setEnabledState(readVoiceCallNotificationEnabled());
  }, []);

  const tocandoEntrante = state === 'ringing' && call?.direction === 'inbound';
  const callId = tocandoEntrante ? (call?.id ?? null) : null;
  const remoteNumber = tocandoEntrante ? (call?.remoteNumber ?? '') : '';
  const remoteName = tocandoEntrante ? (call?.remoteName ?? null) : null;

  // Refs pros callbacks e pro callId: o listener e registrado UMA vez. Sem
  // refs, cada render do sticky (que troca a identidade das funcoes) o
  // re-registraria, e a janela entre remove e add perderia o clique.
  const onAcceptRef = useRef(onAccept);
  onAcceptRef.current = onAccept;
  const onRejectRef = useRef(onReject);
  onRejectRef.current = onReject;
  const callIdRef = useRef<string | null>(callId);
  callIdRef.current = callId;

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data as
        | { type?: string; action?: string; callId?: string }
        | undefined;
      if (data?.type !== 'voice-call-action') return;

      // O SW faz BROADCAST: `clients.matchAll` devolve as abas em ordem
      // arbitraria e a primeira pode NAO ser a dona da sessao SIP. Cada aba
      // compara com a chamada que tem em maos; so a dona age. Sem isto,
      // "Atender" iria pra aba errada e a ligacao ficaria tocando.
      if (!data.callId || data.callId !== callIdRef.current) return;

      // `focus()` aqui, e nao no SW, pelo mesmo motivo: quem sabe que e a dona
      // e a aba. Nunca `window.location` — reload mata a chamada.
      //
      // MAS so em accept e no clique do corpo — NAO em reject. Recusar pela
      // notificacao e DISPENSAR a ligacao, nao pedir pra trocar de contexto: o
      // agente esta ocupado em outra janela, e e exatamente essa janela que a
      // feature deveria respeitar. Puxar o foco nesse momento atrapalha quem
      // a feature deveria servir. Atender e diferente — ele vai falar, entao
      // trazer a janela pra frente faz sentido, e precisa vir ANTES do
      // accept pra ja estar na frente quando a chamada conectar.
      if (data.action === 'accept') {
        window.focus();
        onAcceptRef.current();
      } else if (data.action === 'reject') {
        onRejectRef.current();
      } else {
        // Clique no CORPO da notificacao (sem action, ou o 'focus' que o SW
        // manda quando `event.action` vem vazio): decisao explicita de trazer
        // a janela pra frente — e o gesto de "quero ver isso".
        window.focus();
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  // Espelha o `callId` ATUAL num ref, pro listener de `pagehide` (abaixo) ler
  // no disparo em vez de depender do ciclo reativo a `state` — que o pagehide
  // NAO alimenta (ver comentario grande no topo do arquivo). Um ref, nao uma
  // dependencia de efeito: o listener e registrado UMA vez, no mount.
  const activeCallIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeCallIdRef.current = callId;
  }, [callId]);

  // Passo 2 do titulo. O lookup so dispara com a notificacao JA na tela: sem
  // notificacao nao ha nada pra enriquecer, e o modal/card ja faz a propria
  // consulta. Ramal interno nao vai ao ERP (da match espurio no IXC).
  const lookupPhone =
    shownCallId && remoteNumber && !isInternalExtension(remoteNumber) ? remoteNumber : null;
  const { customer } = useCustomerByPhone(lookupPhone);
  const erpName = customer?.nome ?? null;

  // #442 — este gate precisa ser REAVALIADO durante o toque, e nao so no
  // instante em que a chamada chega. A ligacao toca por ~30s e o agente entra e
  // sai de janela nesse meio tempo; nenhuma das deps do efeito de baixo
  // (callId, numero, nome, permissao, preferencia) muda quando isso acontece.
  //
  // Sem isto: (a) chamada que entra com a aba na frente e o agente troca de
  // janela pra atender no sistema do ISP nunca emite aviso — exatamente o
  // cenario que a #435 existe pra cobrir; (b) o aviso emitido com a aba
  // escondida FICA na tela depois que o agente volta, porque
  // `requireInteraction: true` impede que suma sozinho, duplicando o
  // modal/cartao ate a chamada acabar.
  //
  // Mesmo remedio do listener proprio de `pagehide` (mais abaixo): transicao
  // que o ciclo reativo do React nao alcanca ganha listener proprio.
  //
  // State, e nao leitura direta dentro do efeito: e a MUDANCA do valor que
  // precisa reexecutar o efeito. E como e state, `blur`/`focus` repetidos com o
  // valor ja correto nao re-renderizam (React descarta setState com valor
  // identico) — o agente sai e volta a vontade, o aviso nao empilha nem pisca.
  const [emSegundoPlano, setEmSegundoPlano] = useState(false);
  useEffect(() => {
    // Nenhuma ref de controle aqui, de proposito: `useRef(true)` zerada na
    // limpeza NAO volta a `true` no remount que o StrictMode simula. O que este
    // efeito precisa lembrar vive na const local, recriada a cada execucao.
    const avaliar = () => setEmSegundoPlano(estaEmSegundoPlano());
    // Primeira leitura no efeito, nao no render: `document` nao existe no
    // server. Batcha com o `setPermission` do efeito de cima, entao o efeito de
    // mostrar/esconder ja roda com os dois valores certos.
    avaliar();
    window.addEventListener('focus', avaliar);
    window.addEventListener('blur', avaliar);
    document.addEventListener('visibilitychange', avaliar);
    return () => {
      window.removeEventListener('focus', avaliar);
      window.removeEventListener('blur', avaliar);
      document.removeEventListener('visibilitychange', avaliar);
    };
  }, []);

  // Mostrar + esconder, num efeito so.
  useEffect(() => {
    if (!callId) return;
    // Permissao negada/ausente ou aviso desligado no device: no-op SILENCIOSO.
    if (permission !== 'granted' || !enabled) return;
    // Aba visivel E com foco: o modal/card ja esta na cara do agente; um aviso
    // nativo em cima disso e ruido duplicado. Reavaliado DURANTE o toque
    // (#442) — ver o efeito de `emSegundoPlano` logo acima.
    //
    // Voltar pro primeiro plano com o aviso na tela cai na limpeza deste mesmo
    // efeito, que fecha a notificacao: e a decisao de produto da #442. O que
    // serve o atendente que acabou de voltar e o modal/cartao que ele esta
    // olhando; o aviso do sistema, com `requireInteraction: true`, ficaria por
    // cima dele com botoes duplicados ate a ligacao acabar.
    if (!emSegundoPlano) return;

    let cancelado = false;
    const tag = callNotificationTag(callId);

    void mostrarNotificacao({
      title: remoteName || remoteNumber || 'Chamada recebida',
      body: remoteName ? remoteNumber : 'Chamada recebida',
      tag,
      callId,
    }).then((ok) => {
      if (!ok) return;
      // A limpeza pode ter rodado com o `showNotification` AINDA pendente —
      // fila/ring group: outro agente atende e o cancelamento chega em menos
      // de um segundo. Nesse caso o `fecharNotificacao` do cleanup consultou
      // `getNotifications({tag})` antes de a notificacao existir, recebeu lista
      // vazia e nao fechou nada; ela nasce logo depois com
      // `requireInteraction: true` e NAO some mais sozinha — aviso permanente
      // de uma ligacao morta, cujo "Atender" faz broadcast de um callId que
      // nenhuma aba possui. Quem mostrou tarde tem que fechar. Mesma classe do
      // caso do `pagehide` (efeito proprio abaixo), por outra porta.
      if (cancelado) {
        void fecharNotificacao(tag);
        return;
      }
      setShownCallId(callId);
    });

    return () => {
      cancelado = true;
      setShownCallId((prev) => (prev === callId ? null : prev));
      void fecharNotificacao(tag);
    };
  }, [callId, remoteNumber, remoteName, permission, enabled, emSegundoPlano]);

  // `pagehide` fecha a notificacao DIRETO — nao passa pelo efeito de cima, que
  // so limpa quando `state`/`callId` mudam. `cleanupSip('pagehide')`
  // (`use-webphone.ts:163-166`) nunca chama `setState`, entao sem este
  // listener PROPRIO a notificacao (`requireInteraction: true`) fica presa na
  // tela do sistema depois que a chamada ja morreu. Registrado uma unica vez
  // no mount (deps vazias): quem muda e o ref, nao o listener.
  useEffect(() => {
    const onPageHide = () => {
      const id = activeCallIdRef.current;
      if (!id) return;
      void fecharNotificacao(callNotificationTag(id));
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  // Passo 2: o nome do ERP chega DEPOIS. Reemitir com a MESMA tag substitui a
  // notificacao no lugar — nao empilha e nao reinicia o aviso. Efeito separado
  // de proposito: por o `erpName` nas deps do efeito acima faria a limpeza
  // rodar (fechar) antes de reabrir, e a notificacao piscaria.
  useEffect(() => {
    if (!shownCallId || !erpName) return;
    void mostrarNotificacao({
      title: erpName,
      body: remoteNumber,
      tag: callNotificationTag(shownCallId),
      callId: shownCallId,
    });
  }, [shownCallId, erpName, remoteNumber]);

  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    try {
      // SO em gesto do agente. Chamado de dentro de um efeito (ex: no
      // auto-connect do sticky) o Chrome bloqueia em silencio, a permissao vira
      // `denied` permanente e ainda queima a permissao do push do chat, que
      // compartilha a mesma origem.
      const resultado = await Notification.requestPermission();
      setPermission(resultado);
      if (resultado === 'granted') {
        writeVoiceCallNotificationEnabled(true);
        setEnabledState(true);
        // Registra o SW no mesmo gesto: quem nunca ativou o push do chat nao
        // tem registration, e sem ela nao existe showNotification com botoes.
        await ensureServiceWorker();
      }
    } catch {
      /* browser antigo com a API baseada em callback — sem aviso, sem erro */
    }
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    writeVoiceCallNotificationEnabled(next);
    setEnabledState(next);
  }, []);

  const uiState: VoiceNotificationUiState =
    permission === 'unsupported'
      ? 'unsupported'
      : permission === 'denied'
        ? 'denied'
        : permission === 'granted' && enabled
          ? 'on'
          : 'off';

  return { permission, enabled, uiState, requestPermission, setEnabled };
}
